import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import Papa from "papaparse";
import ConfirmationModal from "../../Common/ConfirmationModal";
import API from "../../../util/api";
import {
  adminTable,
  adminTableHeaderCell,
  adminTableDataCell,
} from "../common/AdminTableStyles";
import { storeSubscriptionSchoolLimits } from "../../../sagas/actions";
import { showErrorModal } from "../../../util/general";
import { FILES_BASE_URL } from "../../../util/constant";

const breakpoints = {
  tablet: 768,
  phone: 480,
};

const Container = styled.div`
  background: #f8f8f8;
  border-radius: 5px;
  padding: 1.5em;
  width: 100%;
  max-width: 1000px;
  box-sizing: border-box;
  overflow-x: auto;

  @media (max-width: ${breakpoints.phone}px) {
    padding: 1em;
  }
`;

const Title = styled.h1`
  font-size: 1.5em;
  margin-bottom: 1em;
  color: ${({ colors }) => colors.labelText};

  @media (max-width: ${breakpoints.phone}px) {
    font-size: 1.25em;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1em;
`;

const FormField = styled.div`
  label {
    display: block;
    font-weight: 600;
    margin-bottom: 0.5em;
    color: ${({ colors }) => colors.labelText};
  }

  input[type="file"] {
    width: 100%;
    font-size: 1em;
  }
`;

const SubmitRow = styled.div`
  display: flex;
  gap: 1em;
  margin-top: 2em;
  overflow-x: auto;

  button {
    padding: 0.6em 1.2em;
    font-size: 1em;
    border-radius: 4px;
    border: none;
    cursor: pointer;
    flex: 0 0 auto;
  }

  .back {
    background: ${({ colors }) => colors.cardBackground};
    color: ${({ colors }) => colors.labelText};
    border: 1px solid ${({ colors }) => colors.border};
  }

  .import {
    background: ${({ colors }) => colors.buttonBackground};
    color: white;
    &:hover {
      background: ${({ colors }) => colors.buttonHoverBackground};
    }
  }
`;

const DownloadLink = styled.a`
  display: inline-block;
  margin-top: 1em;
  color: ${({ colors }) => colors.buttonBackground};
  text-decoration: none;
  font-size: 0.9em;
  text-decoration: underline;
  &:hover {
    text-decoration: underline;
  }
`;

const MatchWrapper = styled.div`
  margin-top: 2.5em;
  background: white;
  border-radius: 6px;
  border: 1px solid ${({ colors }) => colors.border};
`;

const MatchHeader = styled.div`
  padding: 1em 1.25em;
  background: ${({ colors }) => colors.cardBackground};
  color: ${({ colors }) => colors.labelText};
  font-weight: 600;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ProgressIndicator = styled.span`
  font-size: 0.85em;
  color: ${({ colors }) => colors.buttonBackground};
  font-weight: 700;
`;

const MatchTable = styled.table`
  ${adminTable};
  font-size: 0.9em;

  thead th {
    ${adminTableHeaderCell};
    text-transform: uppercase;
    font-size: 0.75em;
    letter-spacing: 0.03em;
  }

  tbody td {
    ${adminTableDataCell};
    padding: 0.75em;
  }

  tbody tr:hover {
    background: #f5f6f8;
  }
`;

const CandidateRow = styled.tr`
  background: #fff7d1;
  font-weight: 600;

  td {
    border-bottom-color: #f1e4ad;
  }
`;

const RadioCell = styled.td`
  width: 40px;
  text-align: center;
`;

const ActionCell = styled.td`
  width: 32px;
`;

const MatchFooter = styled.div`
  display: grid;
  gap: 1.5em;
  padding: 1.25em;
  border-top: 1px solid ${({ colors }) => colors.border};
`;

const FooterActions = styled.div`
  display: flex;
  gap: 1em;
  flex-wrap: wrap;
`;

const PrimaryButton = styled.button`
  padding: 0.6em 1.2em;
  font-size: 0.9em;
  border-radius: 4px;
  border: none;
  cursor: pointer;
  background: ${({ colors }) => colors.buttonBackground};
  color: ${({ colors }) => colors.buttonText || "#fff"};

  &:hover {
    background: ${({ colors }) => colors.buttonHover};
  }
`;

const SecondaryButton = styled.button`
  padding: 0.6em 1.2em;
  font-size: 0.9em;
  border-radius: 4px;
  border: 1px solid ${({ colors }) => colors.border};
  background: ${({ colors }) => colors.cardBackground};
  color: ${({ colors }) => colors.labelText};
  cursor: pointer;

  &:hover {
    background: ${({ colors }) => colors.cardBackground};
  }
`;

const NCESRow = styled.div`
  display: grid;
  grid-template-columns: 160px 1fr 140px;
  gap: 0.75em;
  align-items: center;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const NCESInput = styled.input`
  height: 38px;
  padding: 0 0.75em;
  border: 1px solid ${({ colors }) => colors.border};
  border-radius: 4px;
  font-size: 0.95em;
`;

const FooterLinks = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1em;
  font-size: 0.9em;
`;

const FooterLink = styled.a`
  color: ${({ colors }) => colors.buttonBackground};
  font-weight: 600;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const StatusBanner = styled.div`
  margin-top: 1.5em;
  padding: 1em 1.25em;
  border-radius: 4px;
  font-size: 0.95em;
  background: ${({ type }) => (type === "error" ? "#fde8e8" : "#e7f6ea")};
  color: ${({ type }) => (type === "error" ? "#7d1c1c" : "#1d4a1f")};
  border: 1px solid ${({ type }) => (type === "error" ? "#f1b7b7" : "#b8e0c2")};
`;

const LimitWrapper = styled.div`
  margin-top: 1.5em;
  padding: 1.5em;
  border-radius: 6px;
  background: #fff4f4;
  border: 1px solid #f2caca;
  color: #7d1c1c;
  line-height: 1.6;
  font-size: 0.95em;
`;

const LimitHeading = styled.p`
  margin: 0 0 0.75em;
  font-weight: 700;
`;

const LimitParagraph = styled.p`
  margin: 0.5em 0 0;
`;

const LimitEmail = styled.a`
  color: #e09723;
  font-weight: 600;
  text-decoration: underline;
`;

const formatScore = (value) => {
  const num = Number(value);
  if (Number.isNaN(num)) {
    return "";
  }
  return num % 1 === 0 ? String(num) : num.toFixed(2);
};

const normalizeHeaderKey = (key = "") =>
  key
    .replace(/[“”]/g, '"')
    .replace(/[‘’]/g, "'")
    .replace(/\s+/g, " ")
    .replace(/"/g, "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const SCHOOL_HEADER_MAP = {
  school_name: [
    "full school name as stated on district web site pre schools administrators adult schools and early childhood use the school district name",
    "school name",
    "full school name",
  ],
  level: [
    "school level ie elementary middle high k 12 k 8 6 12 or administrator",
    "school level",
    "level",
  ],
  phone: [
    "school phone no ext",
    "school phone no and no ext",
    "school phone",
    "phone",
    "school phone number",
  ],
  address: ["school address", "address", "street address"],
  city: ["school city", "city"],
  state: ["school state", "state"],
  zip: ["school zip", "zip", "postal code"],
  country: ["school country", "country"],
  county: ["school county", "county"],
  district_name: [
    "school district name as stated on district web site if it s a private school please state private",
    "school district name",
    "district name",
  ],
};

const extractValue = (normalizedRow, field) => {
  const aliases = SCHOOL_HEADER_MAP[field] || [];
  for (const alias of aliases) {
    if (normalizedRow.hasOwnProperty(alias)) {
      const raw = normalizedRow[alias];
      if (raw === null || raw === undefined) {
        return "";
      }
      return typeof raw === "string" ? raw.trim() : String(raw).trim();
    }
  }

  return "";
};

const createMatchEntry = (source = {}) => {
  if (source?.__normalizedMatch) {
    return source;
  }

  const scoreValue =
    source.score ?? source.match_score ?? source.address_score ?? source.name_score ?? 0;
  const numericScore = Number(scoreValue);
  const score = Number.isNaN(numericScore) ? 0 : numericScore;

  return {
    __normalizedMatch: true,
    org_id: source.org_id ? String(source.org_id) : "",
    organization: source.org_name || source.organization || "",
    type: source.org_type || source.type || "",
    street: source.street || source.address || "",
    city: source.city || "",
    state: source.state || "",
    zip: source.zip || "",
    phone: source.phone || "",
    score,
    nces: source.nces_school_id || source.nces_id || "",
  };
};

const normalizeMatches = (matches = []) => {
  const unique = [];
  const seen = new Set();

  matches.map(createMatchEntry).forEach((item, index) => {
    const key =
      item.org_id ||
      (item.organization || item.nces
        ? `${item.organization}|${item.nces}`
        : `${item.organization}|${item.street}|${item.city}|${item.state}|${index}`);

    if (!seen.has(key)) {
      seen.add(key);
      unique.push(item);
    }
  });

  return unique.sort((a, b) => b.score - a.score);
};

const buildCreateNewMatchOption = (school) => ({
  __normalizedMatch: true,
  createNew: true,
  org_id: "",
  organization: "None of the Above (Create New Organization)",
  type: "Create New",
  street: school.address || "",
  city: school.city || "",
  state: school.state || "",
  zip: school.zip || "",
  phone: school.phone || "",
  score: -1,
  nces: "",
});

const withChoiceIds = (matches) =>
  matches.map((match, index) => {
    const choiceId = match.createNew
      ? "__create_new__"
      : match.org_id
      ? String(match.org_id)
      : match.nces
      ? `nces-${match.nces}`
      : `match-${index}`;

    return { ...match, choiceId };
  });

const prepareMatches = (matches = []) => withChoiceIds(normalizeMatches(matches));

const buildSearchPayload = (school) => ({
  name: school.school_name || "",
  phone: (school.phone || "").replace(/\D/g, ""),
  street: school.address || "",
  city: school.city || "",
  state: school.state || "",
  zip: school.zip || "",
  country: school.country || "",
});

const buildImportPayload = (orgId, school, invoiceId, customerId) => {
  const normalizedInvoice = Number(invoiceId);
  const normalizedOrg = Number(orgId);
  const payload = {
    invoice_id: Number.isNaN(normalizedInvoice) ? invoiceId : normalizedInvoice,
    schools: [
      {
        org_id: Number.isNaN(normalizedOrg) ? orgId : normalizedOrg,
        level: school.level || "",
        county: school.county || "",
        district_name: school.district_name || "",
      },
    ],
  };

  if (customerId) {
    payload.customer_id = customerId;
  }

  return payload;
};

const buildCreateOrganizationPayload = (school, invoiceId, customerId) => {
  const normalizedInvoice = Number(invoiceId);
  const base = {
    invoice_id: Number.isNaN(normalizedInvoice) ? invoiceId : normalizedInvoice,
    schools: [
      {
        school_name: school.school_name || "",
        phone: (school.phone || "").replace(/\D/g, ""),
        level: school.level || "",
        address: school.address || "",
        city: school.city || "",
        state: school.state || "",
        zip: school.zip || "",
        country: school.country || "",
        county: school.county || "",
        district_name: school.district_name || "",
      },
    ],
  };

  if (customerId) {
    base.customer_id = customerId;
  }

  return base;
};

export default function ImportSchoolSheetForm({ colors, onCancel }) {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [limitsLoading, setLimitsLoading] = useState(false);
  const [confirmImportOpen, setConfirmImportOpen] = useState(false);
  const [resultModal, setResultModal] = useState({
    show: false,
    title: "",
    message: null,
    status: "Success",
  });
  const [statusBanner, setStatusBanner] = useState(null);
  const [matchContext, setMatchContext] = useState(null);
  const [selectedOrgId, setSelectedOrgId] = useState(null);
  const [ncesInput, setNcesInput] = useState("");
  const [progressState, setProgressState] = useState(null);
  const summaryRef = useRef({ successes: [], failures: [] });
  const queueRef = useRef({
    remaining: [],
    total: 0,
    processed: 0,
    current: null,
  });
  const fetchedLimitsInvoiceRef = useRef(null);
  const dispatch = useDispatch();

  const { invoice_id: invoiceId, customer_id: customerId } =
    useSelector((s) => s.auth.selected_subscriptions) || {};
  const { allowed: storedAllowed, total: storedTotal, invoiceId: storedInvoiceId } =
    useSelector((s) => s.auth.subscription_school_limits) || {};
  const normalizedInvoiceId = invoiceId ? String(invoiceId) : "";
  const normalizedStoredInvoiceId = storedInvoiceId
    ? String(storedInvoiceId)
    : null;
  const allowedLimit = (() => {
    if (storedAllowed === null || storedAllowed === undefined) {
      return null;
    }
    const value = Number(storedAllowed);
    return Number.isFinite(value) ? value : null;
  })();
  const totalCount = (() => {
    if (storedTotal === null || storedTotal === undefined) {
      return null;
    }
    const value = Number(storedTotal);
    return Number.isFinite(value) ? value : null;
  })();
  const limitsMatchSubscription =
    normalizedInvoiceId &&
    normalizedStoredInvoiceId &&
    normalizedInvoiceId === normalizedStoredInvoiceId;
  const hasLimitData =
    limitsMatchSubscription && allowedLimit !== null && allowedLimit > 0;
  const hasTotalData =
    limitsMatchSubscription && totalCount !== null;
  const hasLimitsInfoForInvoice =
    limitsMatchSubscription && (allowedLimit !== null || totalCount !== null);
  const limitReached =
    hasLimitData && hasTotalData && totalCount >= allowedLimit;
  const limitDisplayNumber =
    allowedLimit !== null ? allowedLimit : 0;

  useEffect(() => {
    if (!normalizedInvoiceId) {
      fetchedLimitsInvoiceRef.current = null;
      return;
    }
    if (
      fetchedLimitsInvoiceRef.current &&
      fetchedLimitsInvoiceRef.current !== normalizedInvoiceId
    ) {
      fetchedLimitsInvoiceRef.current = null;
    }
  }, [normalizedInvoiceId]);

  useEffect(() => {
    if (!normalizedInvoiceId || !invoiceId) {
      return;
    }

    if (hasLimitsInfoForInvoice) {
      fetchedLimitsInvoiceRef.current = normalizedInvoiceId;
      return;
    }

    if (limitsLoading) {
      return;
    }

    let cancelled = false;
    setLimitsLoading(true);
    fetchedLimitsInvoiceRef.current = normalizedInvoiceId;

    API.getSchools({
      invoice_id: invoiceId,
      page: 0,
      items_per_page: 1,
    })
      .then((resp) => {
        if (cancelled) {
          return;
        }
        dispatch(
          storeSubscriptionSchoolLimits({
            allowed: resp.allowed_schools,
            total: resp.total,
            invoiceId,
          })
        );
      })
      .catch((err) => {
        if (cancelled) {
          return;
        }
        console.error("Failed to fetch subscription school limits:", err);
        fetchedLimitsInvoiceRef.current = null;
      })
      .finally(() => {
        if (!cancelled) {
          setLimitsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    normalizedInvoiceId,
    invoiceId,
    hasLimitsInfoForInvoice,
    dispatch,
    limitsLoading,
  ]);

  const highlightedCandidate = useMemo(() => matchContext?.candidate, [
    matchContext,
  ]);

  const progressDisplay =
    progressState && progressState.total > 1
      ? `${Math.min(progressState.current || 1, progressState.total)}/${progressState.total}`
      : null;

  const showLimitNotice = !matchContext && limitReached;

  const handleFormSubmit = (event) => {
    event.preventDefault();
    if (showLimitNotice) {
      return;
    }
    if (!invoiceId) {
      showErrorModal(
        "Missing subscription context. Please select a subscription before importing."
      );
      return;
    }
    if (!file) {
      showErrorModal("Please select a CSV file before importing.");
      return;
    }
    setConfirmImportOpen(true);
  };

  const resetToUpload = () => {
    setMatchContext(null);
    setSelectedOrgId(null);
    setFile(null);
    setNcesInput("");
    setProgressState(null);
    summaryRef.current = { successes: [], failures: [] };
    queueRef.current = {
      remaining: [],
      total: 0,
      processed: 0,
      current: null,
    };
  };

  const finalizeProcessing = () => {
    setLoading(false);
    const { successes, failures } = summaryRef.current;
    const successCount = successes.length;
    const failureCount = failures.length;

    let messageNode;
    if (!successCount && !failureCount) {
      messageNode = "No rows were processed from the uploaded file.";
    } else if (!failureCount) {
      const label = successCount === 1 ? "school" : "schools";
      messageNode = `Imported ${successCount} ${label} successfully.`;
    } else {
      messageNode = (
        <div>
          {successCount > 0 && (
            <p>
              Imported {successCount} {successCount === 1 ? "school" : "schools"} successfully.
            </p>
          )}
          <p>We could not import:</p>
          <ul>
            {failures.map((item, index) => (
              <li key={`${item.school || "row"}-${index}`}>
                <strong>{item.school || "Unnamed school"}</strong>:{" "}
                {item.reason || "Unknown error"}
              </li>
            ))}
          </ul>
        </div>
      );
    }

    setResultModal({
      show: true,
      title: failureCount ? "Import Completed with Issues" : "Import Complete",
      message: messageNode,
      status: failureCount ? "Failed" : "Success",
    });

    if (failureCount) {
      setStatusBanner({
        type: "error",
        message: `Imported ${successCount} school${successCount === 1 ? "" : "s"} with ${failureCount} issue${failureCount === 1 ? "" : "s"}.`,
      });
    } else {
      setStatusBanner({
        type: "success",
        message: successCount
          ? `Imported ${successCount} school${successCount === 1 ? "" : "s"} successfully.`
          : "No schools were imported.",
      });
    }

    resetToUpload();
  };

  const processNextSchool = async () => {
    const queue = queueRef.current;


    if (queue.current) {
      // Waiting for user action on the current candidate.
      return;
    }

    if (!queue.remaining.length) {
      setProgressState(null);
      finalizeProcessing();
      return;
    }

    setMatchContext(null);
    setSelectedOrgId(null);
    setNcesInput("");

    const current = queue.remaining.shift();
    queue.current = current;

    const { total, processed } = queue;
    const nextIndex = Math.min(total, processed + 1);
    setProgressState(total > 1 ? { total, current: nextIndex } : null);

    setLoading(true);
    setStatusBanner(null);

    try {
      const searchResponse = await API.searchNCES(buildSearchPayload(current));

      if (searchResponse?.orgId) {
        const importResponse = await API.importSchools(
          buildImportPayload(searchResponse.orgId, current, invoiceId, customerId)
        );
        const status = (importResponse?.status || "").toString().toLowerCase();

        if (status === "success") {
          summaryRef.current.successes.push({
            school: current.school_name || "Unnamed school",
            orgId: String(searchResponse.orgId),
          });
        } else {
          summaryRef.current.failures.push({
            school: current.school_name || "Unnamed school",
            reason: importResponse?.message || "Import failed.",
          });
          setStatusBanner({
            type: "error",
            message: importResponse?.message || "Import failed.",
          });
        }

        queue.processed += 1;
        queue.current = null;
        setLoading(false);
        await processNextSchool();
        return;
      }

      const matches = Array.isArray(searchResponse?.result)
        ? searchResponse.result.filter(Boolean)
        : [];

      if (!matches.length) {
        const payload = buildCreateOrganizationPayload(current, invoiceId, customerId);
        const createResponse = await API.importSchoolsList(payload);
        const createStatus = (createResponse?.status || "").toString().toLowerCase();

        if (createStatus === "success") {
          summaryRef.current.successes.push({
            school: current.school_name || "Unnamed school",
            orgId: "new",
          });
          setStatusBanner({
            type: "success",
            message: `${current.school_name || "School"} imported as a new organization.`,
          });
        } else {
          summaryRef.current.failures.push({
            school: current.school_name || "Unnamed school",
            reason: createResponse?.message || "Unable to create a new organization.",
          });
          setStatusBanner({
            type: "error",
            message: createResponse?.message || "Unable to create a new organization.",
          });
        }

        queue.processed += 1;
        queue.current = null;
        setLoading(false);
        await processNextSchool();
        return;
      }

      const preparedMatches = prepareMatches([...matches, buildCreateNewMatchOption(current)]);

      setMatchContext({
        candidate: current,
        matches: preparedMatches,
      });
      setSelectedOrgId(null);
      setNcesInput("");
      setLoading(false);
      setStatusBanner({
        type: "error",
        message:
          "We found possible NCES matches. Please review and select the correct organization.",
      });
    } catch (err) {
      summaryRef.current.failures.push({
        school: current.school_name || "Unnamed school",
        reason: err?.message || "Unexpected error during import.",
      });
      queue.processed += 1;
      queue.current = null;
      setLoading(false);
      setStatusBanner({
        type: "error",
        message: err?.message || "Unable to import this school.",
      });
      await processNextSchool();
    }
  };

  const importCandidateAsNewOrganization = async () => {
    const queue = queueRef.current;
    const candidate = queue.current || matchContext?.candidate;
    if (!candidate) {
      return;
    }

    setLoading(true);

    try {
      const payload = buildCreateOrganizationPayload(candidate, invoiceId, customerId);
      const response = await API.importSchoolsList(payload);
      const status = (response?.status || "").toString().toLowerCase();

      if (status !== "success") {
        const reason = response?.message || "Unable to create a new organization.";
        setStatusBanner({ type: "error", message: reason });
        setLoading(false);
        return;
      }

      summaryRef.current.successes.push({
        school: candidate.school_name || "Unnamed school",
        orgId: "new",
      });

      setStatusBanner({
        type: "success",
        message: `${candidate.school_name || "School"} imported as a new organization.`,
      });

      queue.processed += 1;
      queue.current = null;
      setMatchContext(null);
      setSelectedOrgId(null);
      setNcesInput("");
      setLoading(false);
      await processNextSchool();
    } catch (err) {
      summaryRef.current.failures.push({
        school: candidate.school_name || "Unnamed school",
        reason: err?.message || "Unable to create a new organization.",
      });
      setStatusBanner({
        type: "error",
        message: err?.message || "Unable to create a new organization.",
      });
      queue.processed += 1;
      queue.current = null;
      setMatchContext(null);
      setSelectedOrgId(null);
      setNcesInput("");
      setLoading(false);
      await processNextSchool();
    }
  };

  const handleImportSelectedMatch = async () => {
    if (!matchContext) {
      return;
    }

    if (!selectedOrgId) {
      setStatusBanner({
        type: "error",
        message: "Select a school from the list before continuing.",
      });
      return;
    }

    const chosen = matchContext.matches.find((item) => item.choiceId === selectedOrgId);
    if (!chosen) {
      setStatusBanner({
        type: "error",
        message: "The selected school could not be found. Please try again.",
      });
      return;
    }

    if (chosen.createNew) {
      await importCandidateAsNewOrganization();
      return;
    }

    const queue = queueRef.current;
    const candidate = queue.current || matchContext.candidate;
    if (!candidate) {
      return;
    }

    setLoading(true);

    try {
      const response = await API.importSchools(
        buildImportPayload(chosen.org_id, candidate, invoiceId, customerId)
      );
      const status = (response?.status || "").toString().toLowerCase();

      if (status !== "success") {
        const errorMessage = response?.message || "Unable to import the selected school.";
        setStatusBanner({ type: "error", message: errorMessage });
        setLoading(false);
        return;
      }

      summaryRef.current.successes.push({
        school: candidate.school_name || "Unnamed school",
        orgId: chosen.org_id,
      });

      setStatusBanner({
        type: "success",
        message: `${candidate.school_name || "School"} imported successfully.`,
      });

      queue.processed += 1;
      queue.current = null;
      setMatchContext(null);
      setSelectedOrgId(null);
      setNcesInput("");
      setLoading(false);

      await processNextSchool();
    } catch (err) {
      summaryRef.current.failures.push({
        school: candidate.school_name || "Unnamed school",
        reason: err?.message || "Unable to import the selected school.",
      });
      setStatusBanner({
        type: "error",
        message: err?.message || "Unable to import the selected school.",
      });
      queue.processed += 1;
      queue.current = null;
      setMatchContext(null);
      setSelectedOrgId(null);
      setNcesInput("");
      setLoading(false);
      await processNextSchool();
    }
  };

  const handleSelectById = async () => {
    if (!matchContext) {
      return;
    }

    const trimmed = ncesInput.trim();
    if (!trimmed) {
      setStatusBanner({
        type: "error",
        message: "Enter an NCES ID before selecting this option.",
      });
      return;
    }

    setLoading(true);

    try {
      const lookupResponse = await API.searchNCESID({ nces_id: trimmed });
      const candidateData =
        lookupResponse?.data ||
        (Array.isArray(lookupResponse?.result) ? lookupResponse.result[0] : null);

      if (!candidateData?.org_id) {
        setStatusBanner({
          type: "error",
          message: "Unable to locate an organization for the provided NCES ID.",
        });
        return;
      }

      const candidateScore = Number(candidateData.score);
      const newMatch = createMatchEntry({
        ...candidateData,
        score: Number.isNaN(candidateScore) ? 100 : candidateScore,
      });

      const newChoiceId = newMatch.createNew
        ? "__create_new__"
        : String(candidateData.org_id);

      setMatchContext((prev) => {
        if (!prev) {
          return prev;
        }
        const combined = prepareMatches([...(prev.matches || []), newMatch]);
        return {
          ...prev,
          matches: combined,
        };
      });

      setSelectedOrgId(newChoiceId);
      setNcesInput(trimmed);
      setStatusBanner({
        type: "success",
        message: "NCES ID located. Select the entry below to continue the import.",
      });
    } catch (err) {
      setStatusBanner({
        type: "error",
        message: err?.message || "Unable to look up the provided NCES ID.",
      });
    } finally {
      setLoading(false);
    }
  };

  const performImport = () => {
    setConfirmImportOpen(false);
    if (!file) {
      return;
    }

    summaryRef.current = { successes: [], failures: [] };
    setMatchContext(null);
    setSelectedOrgId(null);
    setNcesInput("");
    setProgressState(null);
    setLoading(true);
    setStatusBanner(null);

    Papa.parse(file, {
      header: true,
      skipEmptyLines: "greedy",
      complete: async ({ data, errors }) => {
        const fatalErrors = (errors || []).filter(
          (err) => err.type !== "FieldMismatch"
        );

        if (fatalErrors.length) {
          setLoading(false);
          setResultModal({
            show: true,
            title: "Parse Error",
            message:
              fatalErrors[0]?.message || "Could not parse the CSV. Please check its format.",
            status: "Failed",
          });
          return;
        }

        try {
          const schools = (data || [])
            .map((row = {}) => {
              const normalizedRow = Object.entries(row || {}).reduce(
                (acc, [key, value]) => {
                  if (key === "__parsed_extra") {
                    if (Array.isArray(value)) {
                      value.forEach((extra, index) => {
                        acc[`__extra_${index}`] = extra;
                      });
                    }
                    return acc;
                  }
                  const normalizedKey = normalizeHeaderKey(key);
                  if (!normalizedKey) {
                    return acc;
                  }
                  acc[normalizedKey] = value;
                  return acc;
                },
                {}
              );

              const fallbackSchoolName = normalizedRow.__extra_0;
              const schoolName = extractValue(normalizedRow, "school_name") || fallbackSchoolName;
              if (!schoolName) {
                return null;
              }

              if (!normalizedRow["school phone no ext"] && normalizedRow.__extra_1) {
                normalizedRow["school phone no ext"] = normalizedRow.__extra_1;
              }

              if (!normalizedRow["school address"] && normalizedRow.__extra_2) {
                normalizedRow["school address"] = normalizedRow.__extra_2;
              }

              return {
                school_name: schoolName,
                level: extractValue(normalizedRow, "level").toUpperCase(),
                phone: extractValue(normalizedRow, "phone").replace(/[^0-9]/g, ""),
                address:
                  extractValue(normalizedRow, "address") ||
                  normalizedRow.__extra_2 ||
                  "",
                city: extractValue(normalizedRow, "city") || normalizedRow.__extra_3 || "",
                state: extractValue(normalizedRow, "state") || normalizedRow.__extra_4 || "",
                zip: extractValue(normalizedRow, "zip") || normalizedRow.__extra_5 || "",
                country:
                  extractValue(normalizedRow, "country") || normalizedRow.__extra_6 || "",
                county: extractValue(normalizedRow, "county") || normalizedRow.__extra_7 || "",
                district_name:
                  extractValue(normalizedRow, "district_name") ||
                  normalizedRow.__extra_8 ||
                  "",
              };
            })
            .filter(Boolean);

          if (!schools.length) {
            setLoading(false);
            setStatusBanner({
              type: "error",
              message: "No rows detected in the uploaded file.",
            });
            return;
          }

          queueRef.current = {
            remaining: [...schools],
            total: schools.length,
            processed: 0,
            current: null,
          };

          await processNextSchool();
        } catch (err) {
          setLoading(false);
          setResultModal({
            show: true,
            title: "Import Failed",
            message: err?.message || "An error occurred during import.",
            status: "Failed",
          });
        }
      },
      error: () => {
        setLoading(false);
        setResultModal({
          show: true,
          title: "Parse Error",
          message: "Could not parse the CSV. Please check its format.",
          status: "Failed",
        });
      },
    });
  };

  const closeResultModal = () => {
    setResultModal((m) => ({ ...m, show: false, status: "Success" }));
  };

  return (
    <Container>
      <ConfirmationModal
        show={confirmImportOpen}
        title="Confirm Import"
        message="Are you sure you want to import this schoolsheet?"
        onCancel={() => setConfirmImportOpen(false)}
        onConfirm={performImport}
      />

      <ConfirmationModal
        show={resultModal.show}
        title={resultModal.title}
        message={resultModal.message}
        onCancel={closeResultModal}
        onConfirm={closeResultModal}
        status={resultModal.status}
        single
      />

      {statusBanner && (
        <StatusBanner type={statusBanner.type}>{statusBanner.message}</StatusBanner>
      )}

      {!matchContext && (
        <>
          <Title colors={colors}>Upload Schoolsheet</Title>
          {showLimitNotice ? (
            <>
              <LimitWrapper>
                <LimitHeading>Subscription maximum reached.</LimitHeading>
                <LimitParagraph>
                  You've exceeded your subscription's maximum school limit of:{" "}
                  <strong>{limitDisplayNumber}</strong>
                </LimitParagraph>
                <LimitParagraph>
                  Please contact us at:{" "}
                  <LimitEmail href="mailto:subscription@isafe.org">
                    subscription@isafe.org
                  </LimitEmail>{" "}
                  for more information.
                </LimitParagraph>
              </LimitWrapper>

              <SubmitRow colors={colors}>
                <button
                  type="button"
                  className="back"
                  onClick={onCancel}
                  disabled={loading}
                >
                  Cancel
                </button>
              </SubmitRow>
            </>
          ) : (
            <>
              <Form onSubmit={handleFormSubmit}>
                <FormField colors={colors}>
                  <label htmlFor="schoolsheet-file">
                    Please select the schoolsheet you will be importing (max filesize is 50M):
                  </label>
                  <input
                    id="schoolsheet-file"
                    type="file"
                    accept=".csv"
                    onChange={(e) => setFile(e.target.files[0])}
                    disabled={loading || limitsLoading}
                    required
                  />
                </FormField>

                <SubmitRow colors={colors}>
                  <button
                    type="button"
                    className="back"
                    onClick={onCancel}
                    disabled={loading}
                  >
                    Back
                  </button>
                  <button
                    type="submit"
                    className="import"
                    disabled={loading || limitsLoading}
                  >
                    {loading ? "Importing…" : "Import"}
                  </button>
                </SubmitRow>
              </Form>

              <DownloadLink
                colors={colors}
                href={`${FILES_BASE_URL}/ISAFE_SCHOOL_Sheet.csv`}
                target="_blank"
                rel="noopener"
              >
                Download Sample
              </DownloadLink>
            </>
          )}
        </>
      )}

      {matchContext && (
        <MatchWrapper colors={colors}>
          <MatchHeader colors={colors}>
            <span>NCES matches for review</span>
            {progressDisplay && (
              <ProgressIndicator colors={colors}>{progressDisplay}</ProgressIndicator>
            )}
          </MatchHeader>

          <MatchTable colors={colors}>
            <thead>
              <tr>
                <th></th>
                <th>Organization</th>
                <th>Type</th>
                <th>Street</th>
                <th>City</th>
                <th>State</th>
                <th>Zip</th>
                <th>Phone</th>
                <th>Match Score</th>
                <th>NCES</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <CandidateRow>
                <RadioCell />
                <td>{highlightedCandidate?.school_name || "—"}</td>
                <td>{highlightedCandidate?.level || "—"}</td>
                <td>{highlightedCandidate?.address || "—"}</td>
                <td>{highlightedCandidate?.city || "—"}</td>
                <td>{highlightedCandidate?.state || "—"}</td>
                <td>{highlightedCandidate?.zip || "—"}</td>
                <td>{highlightedCandidate?.phone || "—"}</td>
                <td>—</td>
                <td>Pending</td>
                <ActionCell></ActionCell>
              </CandidateRow>

              {matchContext.matches.map((match, index) => {
                const choiceId =
                  match.choiceId ||
                  (match.org_id ? String(match.org_id) : match.nces ? `nces-${match.nces}` : `match-${index}`);
                const isCreateNew = Boolean(match.createNew);
                const disabled = isCreateNew ? false : !match.org_id;
                const selectionDisabled = disabled || loading;
                const rowKey = choiceId || `match-${index}`;
                return (
                  <tr key={rowKey}>
                    <RadioCell>
                      <input
                        type="radio"
                        name="match"
                        value={choiceId}
                        disabled={selectionDisabled}
                        checked={selectedOrgId === choiceId}
                        onChange={() => !selectionDisabled && setSelectedOrgId(choiceId)}
                      />
                    </RadioCell>
                    <td>{match.organization || (isCreateNew ? "Create new organization" : "—")}</td>
                    <td>{match.type || (isCreateNew ? "Create New" : "—")}</td>
                    <td>{match.street || (isCreateNew ? highlightedCandidate?.address || "—" : "—")}</td>
                    <td>{match.city || (isCreateNew ? highlightedCandidate?.city || "—" : "—")}</td>
                    <td>{match.state || (isCreateNew ? highlightedCandidate?.state || "—" : "—")}</td>
                    <td>{match.zip || (isCreateNew ? highlightedCandidate?.zip || "—" : "—")}</td>
                    <td>{match.phone || (isCreateNew ? highlightedCandidate?.phone || "—" : "—")}</td>
                    <td>{isCreateNew ? "—" : formatScore(match.score) || "—"}</td>
                    <td>{match.nces || "—"}</td>
                    <ActionCell></ActionCell>
                  </tr>
                );
              })}
            </tbody>
          </MatchTable>

          <MatchFooter colors={colors}>
            <FooterActions>
              <PrimaryButton type="button" colors={colors} onClick={handleImportSelectedMatch} disabled={loading}>
                Import Selected
              </PrimaryButton>
              <SecondaryButton
                type="button"
                colors={colors}
                onClick={() => {
                  resetToUpload();
                  setStatusBanner({
                    type: "error",
                    message:
                      "Import aborted. You can make edits to your file and try again when ready.",
                  });
                }}
                disabled={loading}
              >
                Abort import
              </SecondaryButton>
            </FooterActions>

            <NCESRow>
              <span>or enter NCES ID</span>
              <NCESInput
                colors={colors}
                value={ncesInput}
                onChange={(e) => setNcesInput(e.target.value)}
                placeholder="e.g. 123456"
                disabled={loading}
              />
              <PrimaryButton type="button" colors={colors} onClick={handleSelectById} disabled={loading}>
                Import by NCES ID
              </PrimaryButton>
            </NCESRow>

            <FooterLinks>
              <FooterLink
                colors={colors}
                href="http://nces.ed.gov/ccd/schoolsearch/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Public School lookup
              </FooterLink>
              <FooterLink
                colors={colors}
                href="http://nces.ed.gov/ccd/districtsearch/"
                target="_blank"
                rel="noopener noreferrer"
              >
                District lookup
              </FooterLink>
              <FooterLink
                colors={colors}
                href="http://nces.ed.gov/surveys/pss/privateschoolsearch/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Private school lookup
              </FooterLink>
            </FooterLinks>
          </MatchFooter>
        </MatchWrapper>
      )}
    </Container>
  );
}
